/**
 * Fetches the FieldDefinition registry from
 * `GET /sysconfig/api/v1/organizations/{org_id}/assignments/condition_fields`
 * scoped to the current rule's entity / LOB / sub-org selection.
 *
 * Thin adapter over [[useConditionFieldsFetcher]] — pre-fills the assignments
 * URL and the assignments-specific param set. The fetcher owns cancellation,
 * the value-digest debounce, and the `enabled`-gated state reset; keep
 * fixes/changes to those behaviours there so they stay identical across every
 * catalog consumer.
 *
 * `enabled` is the caller-owned gate: skip the fetch entirely (and reset state)
 * when prerequisites aren't met. The hook doesn't decide what "ready" means —
 * the BE catalog requires `entity`, sub-org-enabled orgs also require sub-orgs
 * to be picked, etc. The wrapper composes the predicate once and hands the
 * answer in. Same shape as how the pure component takes `prerequisiteMessage`
 * rather than recomputing.
 */

import type { FieldDefinitionDto } from './types';
import { useConditionFieldsFetcher } from './useConditionFieldsFetcher';

export interface UseConditionFieldRegistryParams {
  organizationId: number;
  /** Caller's prerequisite predicate. When false the hook resets state and
   *  does not fire a request. */
  enabled: boolean;
  /** `entity` selects which providers contribute fields ('claims' / 'exposures').
   *  Required by the BE; ignored when `enabled` is false. */
  entity: 'claims' | 'exposures';
  lob: string;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
}

export interface UseConditionFieldRegistryResult {
  fields: FieldDefinitionDto[];
  isLoading: boolean;
  isError: boolean;
}

export const useConditionFieldRegistry = ({
  organizationId,
  enabled,
  entity,
  lob,
  isAllSubOrgs,
  subOrgIds,
}: UseConditionFieldRegistryParams): UseConditionFieldRegistryResult =>
  useConditionFieldsFetcher({
    url: `/sysconfig/api/v1/organizations/${organizationId}/assignments/condition_fields`,
    enabled,
    queryParams: {
      entity,
      lob,
      is_all_sub_orgs: isAllSubOrgs,
      sub_org_ids: subOrgIds ?? [],
    },
  });
